/*
  Evirelle product page: UGC video strip (ugc_videos block in snippets/evi-product-block.liquid).
  - Arrows scroll the strip and only show when there is more to see.
  - Popup mode: a card opens the video in a dialog, with previous / next.
  - Inline mode: a card plays its own video; only one plays at a time.
  Videos are only created on click, so the page never downloads them up front.
*/
if (!customElements.get('evi-ugc-strip')) {
  class EviUgcStrip extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-evi-ugc-track]');
      this.cards = Array.from(this.querySelectorAll('[data-evi-ugc-card]'));
      this.prevButton = this.querySelector('[data-evi-ugc-prev]');
      this.nextButton = this.querySelector('[data-evi-ugc-next]');
      this.dialog = this.querySelector('[data-evi-ugc-dialog]');
      this.controller = new AbortController();
      const { signal } = this.controller;

      this.cards.forEach((card, index) => {
        card.addEventListener('click', () => (this.dataset.mode === 'inline' ? this.toggleInline(card) : this.open(index)), { signal });
      });

      if (this.track) {
        this.prevButton?.addEventListener('click', () => this.scrollByPage(-1), { signal });
        this.nextButton?.addEventListener('click', () => this.scrollByPage(1), { signal });
        this.track.addEventListener('scroll', () => this.updateArrows(), { passive: true, signal });
        this.resizeObserver = new ResizeObserver(() => this.updateArrows());
        this.resizeObserver.observe(this.track);
      }

      if (this.dialog) {
        this.stage = this.dialog.querySelector('[data-evi-ugc-stage]');
        this.dialog.querySelector('[data-evi-ugc-close]')?.addEventListener('click', () => this.dialog.close(), { signal });
        this.dialog.querySelectorAll('[data-evi-ugc-step]').forEach((button) => {
          button.addEventListener('click', () => this.show(this.index + Number(button.dataset.eviUgcStep)), { signal });
        });

        // A click on the backdrop (the dialog itself, not its content) closes it
        this.dialog.addEventListener('click', (event) => {
          if (event.target === this.dialog) this.dialog.close();
        }, { signal });

        this.dialog.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowLeft') this.show(this.index - 1);
          if (event.key === 'ArrowRight') this.show(this.index + 1);
        }, { signal });

        this.dialog.addEventListener('close', () => {
          this.stage.replaceChildren();
          document.documentElement.classList.remove('evi-scroll-lock');
          this.cards[this.index]?.focus({ preventScroll: true });
        }, { signal });
      }
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.resizeObserver?.disconnect();
      if (this.dialog?.open) this.dialog.close();
    }

    videoFor(card) {
      const template = card.parentElement.querySelector('template[data-evi-ugc-video]');
      return template?.content.querySelector('video')?.cloneNode(true);
    }

    /* Popup */
    open(index) {
      if (!this.dialog) return;
      if (!this.dialog.open) {
        this.dialog.showModal();
        document.documentElement.classList.add('evi-scroll-lock');
      }
      this.show(index);
    }

    show(index) {
      const count = this.cards.length;
      this.index = (index + count) % count;

      const video = this.videoFor(this.cards[this.index]);
      if (!video) return;

      video.preload = 'auto';
      video.autoplay = true;
      this.stage.replaceChildren(video);
      video.play().catch(() => {
        // Autoplay with sound was blocked: start muted, the controls let the visitor unmute
        video.muted = true;
        video.play().catch(() => {});
      });

      this.dialog.querySelectorAll('[data-evi-ugc-step]').forEach((button) => { button.hidden = count < 2; });
    }

    /* Inline */
    toggleInline(card) {
      const item = card.parentElement;
      let video = card.querySelector('video[data-evi-ugc-inline]');

      if (!video) {
        video = this.videoFor(card);
        if (!video) return;
        video.removeAttribute('controls');
        video.setAttribute('data-evi-ugc-inline', '');
        video.loop = true;
        video.addEventListener('pause', () => item.classList.remove('is-playing'));
        video.addEventListener('play', () => item.classList.add('is-playing'));
        card.querySelector('.evi-ugc__play').before(video);
      }

      if (video.paused) {
        this.querySelectorAll('video[data-evi-ugc-inline]').forEach((other) => other !== video && other.pause());
        video.play().catch(() => {
          video.muted = true;
          video.play().catch(() => {});
        });
      } else {
        video.pause();
      }
    }

    /* Arrows */
    scrollByPage(direction) {
      const rtl = getComputedStyle(this.track).direction === 'rtl' ? -1 : 1;
      this.track.scrollBy({ left: direction * rtl * this.track.clientWidth * 0.8, behavior: 'smooth' });
    }

    updateArrows() {
      const max = this.track.scrollWidth - this.track.clientWidth;
      const position = Math.abs(this.track.scrollLeft);
      if (this.prevButton) this.prevButton.hidden = position <= 2;
      if (this.nextButton) this.nextButton.hidden = position >= max - 2;
    }
  }

  customElements.define('evi-ugc-strip', EviUgcStrip);
}

/*
  Evirelle product page: bundle offer (bundle_offer block in snippets/evi-bundle-offer.liquid, and the horizontal_offer
  block in snippets/evi-horizontal-offer.liquid, which uses the same element with data-layout="horizontal").
  - Offer cards set how many pairs the bundle has and how many are paid.
  - Pair #1 is the product being viewed; every other pair is picked in the popup. Picked pairs stay when
    switching offers, so going from 2 to 5 pairs keeps the first two.
  - Prices assume the cheapest pairs are the free ones (the discount itself is set up in Shopify).
  - Add to cart posts every pair at once, then fires the theme's variant:add / cart:change events so the
    cart drawer opens and refreshes like with the normal button.
*/
if (!customElements.get('evi-bundle')) {
  const escapeHtml = (value) => String(value ?? '').replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]);

  const formatMoney = (cents) => {
    const format = (window.themeVariables?.settings?.moneyFormat || '{{amount}}').replace(/<[^>]*>/g, '');
    const value = cents / 100;
    // Like Liquid's money_without_trailing_zeros: 598 kr, not 598,00 kr
    const group = (decimals, thousands = ',', decimal = '.') => {
      if (cents % 100 === 0) decimals = 0;
      const [whole, fraction] = value.toFixed(decimals).split('.');
      return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (fraction ? decimal + fraction : '');
    };

    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => ({
      amount: group(2),
      amount_no_decimals: group(0),
      amount_with_comma_separator: group(2, '.', ','),
      amount_no_decimals_with_comma_separator: group(0, '.', ','),
      amount_with_space_separator: group(2, ' ', ','),
      amount_no_decimals_with_space_separator: group(0, ' ', ','),
      amount_with_apostrophe_separator: group(2, "'", '.')
    })[key] ?? group(2));
  };

  class EviBundle extends HTMLElement {
    connectedCallback() {
      const dataElement = this.querySelector('[data-bundle-data]');
      if (!dataElement) return;

      this.data = JSON.parse(dataElement.textContent);
      this.labels = this.data.labels;
      this.offers = Array.from(this.querySelectorAll('[data-offer]'));
      this.slots = Array.from(this.querySelectorAll('[data-slot]'));
      this.picker = this.querySelector('[data-bundle-picker]');
      this.atc = this.querySelector('[data-bundle-atc]');
      this.atcText = this.querySelector('[data-bundle-atc-text]');
      this.error = this.querySelector('[data-bundle-error]');

      this.variants = {};
      Object.entries(this.data.products).forEach(([productId, product]) => {
        product.variants.forEach((variant) => { this.variants[variant.id] = { ...variant, productId }; });
      });

      this.selections = this.slots.map(() => null);
      this.selections[0] = { productId: String(this.data.current), variantId: this.data.currentVariant };

      this.controller = new AbortController();
      const { signal } = this.controller;

      this.addEventListener('change', (event) => this.onChange(event), { signal });
      this.addEventListener('click', (event) => this.onClick(event), { signal });

      if (this.picker) {
        this.picker.addEventListener('click', (event) => {
          if (event.target === this.picker) this.picker.close();
        }, { signal });
        this.picker.addEventListener('close', () => document.documentElement.classList.remove('evi-scroll-lock'), { signal });
      }

      this.render({ animate: false });
    }

    disconnectedCallback() {
      this.controller?.abort();
      if (this.picker?.open) this.picker.close();
    }

    get checkedOffer() {
      return this.offers.find((offer) => offer.querySelector('.evi-offer__input')?.checked);
    }

    get offer() {
      const checked = this.checkedOffer || this.offers[0];
      if (!checked) return { pairs: 1, paid: 1 };
      return { pairs: Number(checked.dataset.pairs), paid: Number(checked.dataset.paid) };
    }

    /* Events */
    onChange(event) {
      const target = event.target;

      if (target.matches('.evi-offer__input')) {
        this.hideError();
        this.render({ animate: true });
      } else if (target.matches('[data-slot-variant]')) {
        const index = Number(target.closest('[data-slot]').dataset.slot);
        this.selections[index].variantId = Number(target.value);
        this.renderSlot(index);
        this.renderPrices();
      } else if (target.matches('[data-picker-variant]')) {
        this.renderPickerPrice(target.closest('[data-picker-item]'));
      }
    }

    onClick(event) {
      const target = event.target.closest('button');
      if (!target) return;

      if (target.matches('[data-slot-choose], [data-slot-change]')) {
        this.openPicker(Number(target.closest('[data-slot]').dataset.slot));
      } else if (target.matches('[data-picker-close]')) {
        this.picker.close();
      } else if (target.matches('[data-picker-add]')) {
        this.pick(target.closest('[data-picker-item]'));
      } else if (target.matches('[data-bundle-atc]')) {
        this.addToCart();
      }
    }

    /* Rendering */
    render({ animate }) {
      const { pairs, paid } = this.offer;
      const count = this.querySelector('[data-bundle-count]');
      if (count) count.textContent = this.labels.pairs.replace('[pairs]', pairs);

      // Horizontal offer (snippets/evi-horizontal-offer.liquid): the pairs open inside the selected offer row
      if (this.dataset.layout === 'horizontal') {
        const builder = this.querySelector('[data-bundle-builder]');
        const panel = this.checkedOffer?.querySelector('[data-offer-panel]');
        if (builder && panel && builder.parentElement !== panel) {
          panel.append(builder);
          if (animate) {
            builder.classList.remove('is-opening');
            void builder.offsetWidth;
            builder.classList.add('is-opening');
          }
        }
      }

      this.slots.forEach((slot, index) => {
        const wasHidden = slot.hidden;
        slot.hidden = index >= pairs;
        slot.classList.toggle('is-free', index >= paid);

        if (animate && wasHidden && !slot.hidden) {
          slot.style.setProperty('--evi-slot-delay', `${(index - 1) * 60}ms`);
          slot.classList.remove('is-entering');
          void slot.offsetWidth;
          slot.classList.add('is-entering');
        }

        if (index > 0) this.renderSlot(index);
      });

      this.renderPrices();
    }

    renderSlot(index) {
      const slot = this.slots[index];
      const body = slot.querySelector('[data-slot-body]');
      const selection = this.selections[index];

      if (index === 0) {
        const variant = this.variants[selection.variantId];
        const image = slot.querySelector('.evi-slot__media img');
        if (variant?.image && image) {
          image.removeAttribute('srcset');
          image.src = variant.image;
        }
        return;
      }

      if (!selection) {
        body.innerHTML = `<button type="button" class="evi-slot__choose" data-slot-choose>${escapeHtml(this.labels.choose)}</button>`;
        return;
      }

      const product = this.data.products[selection.productId];
      const variant = this.variants[selection.variantId];
      const image = variant?.image || product.image;
      const options = product.variants.map((option) => `<option value="${option.id}"${option.id === selection.variantId ? ' selected' : ''}${option.available ? '' : ' disabled'}>${escapeHtml(option.title)}</option>`).join('');

      body.innerHTML = `
        <div class="evi-slot__pick">
          <span class="evi-slot__media">${image ? `<img src="${escapeHtml(image)}" alt="" width="120" height="120" loading="lazy">` : ''}</span>
          <span class="evi-slot__info">
            <span class="evi-slot__title-row">
              <span class="evi-slot__title">${escapeHtml(product.title)}</span>
              <button type="button" class="evi-slot__change" data-slot-change>${escapeHtml(this.labels.change)}</button>
            </span>
            ${product.single ? '' : `<span class="evi-select"><select data-slot-variant aria-label="${escapeHtml(product.title)}">${options}</select></span>`}
          </span>
        </div>`;
    }

    /* Bundle total: every pair's price, minus the cheapest ones that are free. Empty pairs count at this product's price. */
    totals() {
      const { pairs, paid } = this.offer;
      const prices = [];
      for (let index = 0; index < pairs; index += 1) {
        const selection = this.selections[index];
        prices.push(selection ? this.variants[selection.variantId]?.price ?? this.data.fallbackPrice : this.data.fallbackPrice);
      }
      prices.sort((a, b) => b - a);
      const total = prices.slice(0, paid).reduce((sum, price) => sum + price, 0);
      return { total, pairs, paid };
    }

    /* Full price of a pair: the higher of price and compare-at price */
    fullPrice(variant) {
      if (!variant) return this.data.strikePrice;
      return Math.max(variant.price, variant.compare || 0);
    }

    renderPrices() {
      const { total, pairs } = this.totals();
      const current = this.checkedOffer;
      const perPairMode = this.data.priceDisplay === 'per_pair';

      // Full price of the bundle being built (empty pairs count at this product's full price)
      let currentFull = 0;
      for (let index = 0; index < pairs; index += 1) {
        const selection = this.selections[index];
        currentFull += selection ? this.fullPrice(this.variants[selection.variantId]) : this.data.strikePrice;
      }

      this.offers.forEach((offer) => {
        const offerPairs = Number(offer.dataset.pairs);
        const offerPaid = Number(offer.dataset.paid);
        const offerTotal = offer === current ? total : this.data.fallbackPrice * offerPaid;
        const offerFull = offer === current ? currentFull : this.data.strikePrice * offerPairs;

        const shown = perPairMode ? Math.round(offerTotal / offerPairs) : offerTotal;
        const shownCompare = perPairMode ? Math.round(offerFull / offerPairs) : offerFull;

        const price = offer.querySelector('[data-offer-price]');
        const compare = offer.querySelector('[data-offer-compare]');
        const savings = offer.querySelector('[data-offer-savings]');

        if (price) price.textContent = formatMoney(shown);
        if (compare) {
          compare.textContent = formatMoney(shownCompare) + (perPairMode ? this.labels.perPair || '' : '');
          compare.hidden = shownCompare <= shown;
        }
        if (savings) {
          const saved = offerFull - offerTotal;
          savings.textContent = savings.dataset.template.replace('[savings]', formatMoney(saved));
          savings.hidden = saved <= 0;
        }
      });

      if (this.atc && !this.atc.disabled) {
        this.atcText.textContent = `${this.labels.atc} – ${formatMoney(total)}`;
      }
    }

    /* Popup */
    openPicker(index) {
      if (!this.picker) return;
      this.target = index;
      const { paid } = this.offer;
      const free = index >= paid;

      const subtitle = this.picker.querySelector('[data-picker-subtitle]');
      if (subtitle) {
        subtitle.textContent = this.labels.pickerSubtitle
          .replace('[n]', index + 1)
          .replace('[price]', free ? this.labels.free : '')
          .replace(/\s*[·\-–]\s*$/, '');
      }

      // Start every list on the size picked for pair #1, people usually want the same size
      const firstTitle = this.variants[this.selections[0].variantId]?.title;

      this.picker.querySelectorAll('[data-picker-item]').forEach((item) => {
        const select = item.querySelector('select[data-picker-variant]');
        if (select && firstTitle) {
          const match = Array.from(select.options).find((option) => option.text === firstTitle && !option.disabled);
          if (match) select.value = match.value;
        }

        const selection = this.selections[index];
        item.classList.toggle('is-current', Boolean(selection && String(selection.productId) === item.dataset.pickerItem));
        this.renderPickerPrice(item);
      });

      if (!this.picker.open) {
        this.picker.showModal();
        document.documentElement.classList.add('evi-scroll-lock');
      }
    }

    renderPickerPrice(item) {
      const field = item.querySelector('[data-picker-variant]');
      const variant = this.variants[field?.value];
      const priceElement = item.querySelector('[data-picker-price]');
      if (!variant || !priceElement) return;

      const free = this.target >= this.offer.paid;
      priceElement.classList.toggle('is-free', free);
      priceElement.innerHTML = free && this.labels.free
        ? `${escapeHtml(this.labels.free)} <s>${formatMoney(variant.price)}</s>`
        : formatMoney(variant.price);
    }

    pick(item) {
      const field = item.querySelector('[data-picker-variant]');
      const variant = this.variants[field?.value];
      if (!variant || !variant.available) return;

      const index = this.target;
      this.selections[index] = { productId: item.dataset.pickerItem, variantId: variant.id };
      this.picker.close();
      this.hideError();
      this.renderSlot(index);
      this.renderPrices();

      const slot = this.slots[index];
      slot.classList.remove('is-filled');
      void slot.offsetWidth;
      slot.classList.add('is-filled');
      slot.querySelector('[data-slot-change]')?.focus({ preventScroll: true });
    }

    /* Add to cart */
    async addToCart() {
      if (this.busy || this.atc.disabled) return;

      const { pairs } = this.offer;
      const empty = this.selections.slice(0, pairs).findIndex((selection) => !selection);
      if (empty !== -1) {
        const slot = this.slots[empty];
        slot.classList.remove('is-missing');
        void slot.offsetWidth;
        slot.classList.add('is-missing');
        this.openPicker(empty);
        return;
      }

      const quantities = new Map();
      this.selections.slice(0, pairs).forEach(({ variantId }) => quantities.set(variantId, (quantities.get(variantId) || 0) + 1));
      const items = Array.from(quantities, ([id, quantity]) => ({ id, quantity }));

      const sections = ['variant-added'];
      document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } }));

      this.busy = true;
      this.hideError();
      this.atc.setAttribute('aria-busy', 'true');

      try {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items, sections: sections.join(','), sections_url: `${root}variants/${items[0].id}` })
        });
        const json = await response.json();

        if (!response.ok) {
          this.showError(json.description || json.message);
          document.dispatchEvent(new CustomEvent('cart:refresh'));
          return;
        }

        if (window.themeVariables?.settings?.cartType === 'page' || window.themeVariables?.settings?.pageType === 'cart') {
          window.location.href = `${root}cart`;
          return;
        }

        const cart = await (await fetch(`${root}cart.js`)).json();
        cart.sections = json.sections;

        this.dispatchEvent(new CustomEvent('variant:add', { bubbles: true, detail: { items: json.items || [json], cart } }));
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', cart } }));
      } catch (error) {
        this.showError();
      } finally {
        this.busy = false;
        this.atc.removeAttribute('aria-busy');
      }
    }

    showError(message) {
      if (!this.error) return;
      this.error.textContent = message || this.labels.error;
      this.error.hidden = false;
    }

    hideError() {
      if (this.error) this.error.hidden = true;
    }
  }

  customElements.define('evi-bundle', EviBundle);
}

/*
  Evirelle product page: delivery estimate (delivery_estimate block, snippets/evi-delivery-estimate.liquid).
  Recalculates the dates from today on every visit, as Shopify serves cached pages.
*/
if (!customElements.get('evi-delivery-estimate')) {
  const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const escapeText = (value) => String(value).replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]);

  class EviDeliveryEstimate extends HTMLElement {
    connectedCallback() {
      const min = Number(this.dataset.min) || 0;
      const max = Math.max(Number(this.dataset.max) || 0, min);
      const businessDays = this.dataset.business === 'true';
      const withWeekday = this.dataset.weekday === 'true';

      // Step forward one day at a time; with business days on, weekends are not counted
      const addDays = (count) => {
        const date = new Date();
        let added = 0;
        while (added < count) {
          date.setDate(date.getDate() + 1);
          const day = date.getDay();
          if (businessDays && (day === 0 || day === 6)) continue;
          added += 1;
        }
        return date;
      };

      const label = (date) => `${withWeekday ? `${WEEKDAYS[date.getDay()]} ` : ''}${date.getDate()} ${MONTHS[date.getMonth()]}`;
      const strong = (date) => `<strong class="evi-delivery__date">${label(date)}</strong>`;

      const text = this.querySelector('[data-evi-delivery-text]');
      if (!text || !this.dataset.template) return;

      text.innerHTML = escapeText(this.dataset.template)
        .replace('[start]', strong(addDays(min)))
        .replace('[end]', strong(addDays(max)));
    }
  }

  customElements.define('evi-delivery-estimate', EviDeliveryEstimate);
}
